import React, { Children, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Briefcase, Home as HomeIcon, Calendar, MonitorPlay, ShoppingBag, Eye, CheckCircle2, Sparkles, Quote, ArrowRight } from 'lucide-react';
const SERVICES = [{
  title: 'Wellness & Lifestyle',
  description: 'Fitness coaching, nutrition, and recovery.',
  icon: Heart,
  color: 'bg-plum text-white'
}, {
  title: 'Professional Support',
  description: 'Virtual assistance and executive support.',
  icon: Briefcase,
  color: 'bg-white text-plum'
}, {
  title: 'Home & Property',
  description: 'Cleaning, management, and relocation.',
  icon: HomeIcon,
  color: 'bg-white text-plum'
}, {
  title: 'Life Management',
  description: 'Travel, events, and personal concierge.',
  icon: Calendar,
  color: 'bg-charcoal text-white'
}, {
  title: 'Digital Learning',
  description: 'Courses for fitness, business, and life.',
  icon: MonitorPlay,
  color: 'bg-white text-plum'
}, {
  title: 'Shop & Style',
  description: 'Premium gym wear and digital guides.',
  icon: ShoppingBag,
  color: 'bg-fuchsia text-white'
}];
const MARQUEE_ITEMS = ['Fitness Coaching', 'Nutrition & Meal Prep', 'Rest & Recovery', 'Virtual Assistant', 'Executive Support', 'Small Business Compliance', 'Cleaning Services', 'Property Management', 'Relocation & Settling-In', 'Travel Planning', 'Event Planning', 'Personal Concierge', 'Digital Learning'];
const TESTIMONIALS = [{
  quote: 'Silvermark completely transformed how I manage my week. I finally have time to breathe.',
  name: 'Sarah J.',
  role: 'Executive Director'
}, {
  quote: 'The fitness coaching combined with meal prep is a game changer for my busy schedule.',
  name: 'Michelle T.',
  role: 'Entrepreneur'
}, {
  quote: 'Professional, reliable, and truly caring. They handle the details so I can focus on the big picture.',
  name: 'Elena R.',
  role: 'Creative Agency Owner'
}, {
  quote: 'I used to feel overwhelmed constantly. Now, I feel clear, focused, and supported.',
  name: 'David K.',
  role: 'Consultant' // Note: Audience is mostly women, let's change to a woman's name
}];
TESTIMONIALS[3] = {
  quote: 'I used to feel overwhelmed constantly. Now, I feel clear, focused, and supported.',
  name: 'Jessica M.',
  role: 'Consultant'
};
// Animation variants
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};
const wordVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
export function Home() {
  const heroHeadline = 'Clarity for the body to host dreams.';
  return <main className="flex flex-col min-h-screen" data-id="element-596" data-mp="oe4bojbqo7yj">
      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-plum pt-20" data-id="element-597" data-mp="og8dmvq2uww6">
        {/* Background Image & Gradient Overlay */}
        <div className="absolute inset-0 z-0" data-id="element-598" data-mp="3st57n8rqzs40">
          <img src="https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?q=80&w=2070&auto=format&fit=crop" alt="Confident woman stretching" className="w-full h-full object-cover opacity-20" data-id="element-599" data-mp="3nt941amyq2oz" />
          <div className="absolute inset-0 bg-gradient-to-b from-plum/80 via-plum/90 to-plum" data-id="element-600" data-mp="ni8p6glddve" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-fuchsia/20 via-transparent to-transparent" data-id="element-601" data-mp="1h1rvhw56quk4" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-602" data-mp="27eeq0aobh1hx">
          <motion.h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-tight tracking-tight" variants={containerVariants} initial="hidden" animate="visible" data-id="element-603" data-mp="2ccpxwfv1x1xi">
            {heroHeadline.split(' ').map((word, i) => <motion.span key={i} className="inline-block mr-[0.25em]" variants={wordVariants} data-id="element-604" data-mp="2qdixhf07mn7h" data-mpi={i}>
                {word}
              </motion.span>)}
          </motion.h1>

          <motion.p className="text-lg md:text-xl text-warmwhite/80 max-w-2xl mx-auto mb-10 font-body leading-relaxed" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1,
          duration: 0.8
        }} data-id="element-605" data-mp="304nbabe5g20q">
            We simplify life for the woman who does everything — so you have
            energy left for what matters most.
          </motion.p>

          <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.2,
          duration: 0.8
        }} data-id="element-606" data-mp="tdiby8s5rz2h">
            <Link to="/services" className="w-full sm:w-auto px-8 py-4 bg-fuchsia text-white rounded-full font-medium hover:bg-fuchsia-light transition-colors shadow-lg hover:shadow-xl text-center" data-id="element-607" data-mp="1ccw6artsvbti">
              Explore Services
            </Link>
            <Link to="/community" className="w-full sm:w-auto px-8 py-4 bg-transparent border border-white/30 text-white rounded-full font-medium hover:bg-white/10 transition-colors text-center" data-id="element-608" data-mp="202qid76aowcp">
              Join the Community
            </Link>
          </motion.div>
        </div>
      </section>

      {/* SERVICES OVERVIEW (Bento Grid) */}
      <section className="py-24 bg-warmwhite" data-id="element-609" data-mp="3kiydume415cu">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-610" data-mp="3piu9nycnlqhb">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUpVariants} className="text-center mb-16" data-id="element-611" data-mp="3amv2cpxdbud8">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-plum mb-4" data-id="element-612" data-mp="31nzz9mpmf0e5">
              Everything you need.
              <br data-id="element-613" data-mp="zutldbpkhw6x" />
              One trusted team.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-614" data-mp="14tmy31x15j61">
            {SERVICES.map((service, index) => {
            const Icon = service.icon;
            return <motion.div key={service.title} initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={{
              hidden: {
                opacity: 0,
                y: 20
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  delay: index * 0.1
                }
              }
            }} className={`group relative p-8 rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${service.color} ${service.color.includes('bg-white') ? 'border border-charcoal/5 shadow-sm' : ''}`} data-id="element-615" data-mp="19yh1vp6f3q2m" data-mpi={index}>
                  <Icon className="w-10 h-10 mb-6 opacity-90" data-id="element-616" data-mp="wubfedkxljfd" />
                  <h3 className="font-display text-2xl font-bold mb-3" data-id="element-617" data-mp="wtteh3nbqh14">
                    {service.title}
                  </h3>
                  <p className="font-body opacity-80 mb-8" data-id="element-618" data-mp="16k4rrgswe2ko">
                    {service.description}
                  </p>

                  <Link to="/services" className="absolute bottom-8 left-8 flex items-center gap-2 font-medium opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300" data-id="element-619" data-mp="1alq1ita4y0qn">
                    Learn more <ArrowRight className="w-4 h-4" data-id="element-620" data-mp="mvdudkmps814" />
                  </Link>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* MARQUEE STRIP */}
      <section className="bg-gold py-6 overflow-hidden flex items-center border-y border-gold-dark/20" data-id="element-621" data-mp="1ybu36v3rjqf0">
        <div className="flex whitespace-nowrap animate-marquee pause-on-hover" data-id="element-622" data-mp="239ceut960jqp">
          {[...Array(2)].map((_, i) => <div key={i} className="flex items-center" data-id="element-623" data-mp="2ddkh1sip6ri9" data-mpi={i}>
              {MARQUEE_ITEMS.map((item, index) => <Fragment key={`${i}-${index}`} data-id="element-624" data-mp="28fra2x9kltgx" data-mpi={index}>
                  <span className="font-display font-bold text-xl md:text-2xl text-charcoal uppercase tracking-wider mx-8" data-id="element-625" data-mp="23nr34kv320au">
                    {item}
                  </span>
                  <span className="w-2 h-2 rounded-full bg-charcoal/30" data-id="element-626" data-mp="1unkupde8rqij" />
                </Fragment>)}
            </div>)}
        </div>
      </section>

      {/* WHY SILVERMARK */}
      <section className="py-24 bg-warmwhite" data-id="element-627" data-mp="1kpex37zgx6jh">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-628" data-mp="1kq6gzljpdftx">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-629" data-mp="1gobeidq1cwek">
            {[{
            icon: Eye,
            title: 'Clarity',
            desc: 'See your life with fresh eyes. We cut through the noise.'
          }, {
            icon: CheckCircle2,
            title: 'Consistency',
            desc: 'Reliable support that shows up — every single time.'
          }, {
            icon: Sparkles,
            title: 'Confidence',
            desc: 'When your life runs smoothly, you dream bigger.'
          }].map((feature, index) => {
            const Icon = feature.icon;
            return <motion.div key={feature.title} initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={{
              hidden: {
                opacity: 0,
                y: 30
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  delay: index * 0.2,
                  duration: 0.6
                }
              }
            }} className="text-center flex flex-col items-center" data-id="element-630" data-mp="3q4j46nhe1fn3" data-mpi={index}>
                  <div className="w-16 h-16 rounded-2xl bg-plum/5 flex items-center justify-center mb-6" data-id="element-631" data-mp="18vpx9rhh8gsh">
                    <Icon className="w-8 h-8 text-plum" data-id="element-632" data-mp="1cw04f7h09d8x" />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-charcoal mb-4" data-id="element-633" data-mp="20mm0e0dqj72k">
                    {feature.title}
                  </h3>
                  <p className="text-charcoal/70 leading-relaxed max-w-xs" data-id="element-634" data-mp="25kx8o6odt9io">
                    {feature.desc}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-warmwhite-dark/30 overflow-hidden" data-id="element-635" data-mp="g5zirhbxncvu">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 text-center" data-id="element-636" data-mp="g8vecz0b5ph4">
          <h2 className="font-display text-4xl font-bold text-plum" data-id="element-637" data-mp="l3iv2bp2lfl5">
            What they say
          </h2>
        </div>

        <div className="relative flex overflow-x-hidden group" data-id="element-638" data-mp="2hvoj0nywv8sp">
          <div className="flex animate-marquee group-hover:pause-on-hover whitespace-nowrap py-4" data-id="element-639" data-mp="2hwggk4c2jglm">
            {[...TESTIMONIALS, ...TESTIMONIALS].map((testimonial, index) => <div key={index} className="w-[350px] md:w-[450px] flex-shrink-0 mx-4 bg-white p-8 rounded-2xl shadow-sm border-l-4 border-fuchsia whitespace-normal" data-id="element-640" data-mp="292l9urvati3v" data-mpi={index}>
                <Quote className="w-8 h-8 text-fuchsia/20 mb-4" data-id="element-641" data-mp="1z3m1laeg3ohe" />
                <p className="text-charcoal/80 text-lg mb-6 leading-relaxed font-body" data-id="element-642" data-mp="1u5c4gu95udt5">
                  "{testimonial.quote}"
                </p>
                <div data-id="element-643" data-mp="1q6lgbcmoa6i6">
                  <p className="font-display font-bold text-plum" data-id="element-644" data-mp="3ok4vefz3mger">
                    {testimonial.name}
                  </p>
                  <p className="text-sm text-charcoal/60" data-id="element-645" data-mp="3ki9z5pw0yt7u">{testimonial.role}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="py-24 bg-plum relative overflow-hidden" data-id="element-646" data-mp="1b9282eg5jlzr">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-fuchsia/20 via-transparent to-transparent" data-id="element-647" data-mp="1g0k912i6dmkq" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-648" data-mp="1k1dttn0bml9e">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUpVariants} data-id="element-649" data-mp="ndh9q85od7tf">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-650" data-mp="wb1evs6nro9b">
              Ready to reclaim your time?
            </h2>
            <p className="text-warmwhite/80 text-lg mb-10 max-w-2xl mx-auto" data-id="element-651" data-mp="27s00payl9zht">
              Join thousands of women who have chosen clarity. Subscribe to our
              newsletter for insights, or book a free discovery call today.
            </p>

            <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" onSubmit={e => e.preventDefault()} data-id="element-652" data-mp="2ba784flsn817">
              <input type="email" placeholder="Enter your email" className="flex-1 px-6 py-4 rounded-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold" required data-id="element-653" data-mp="2kavf4qcrs84l" />
              <button type="submit" className="px-8 py-4 bg-gold text-charcoal font-medium rounded-full hover:bg-gold-light transition-colors shadow-lg" data-id="element-654" data-mp="2p8x7y2zzzlt9">
                Join the Movement
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </main>;
}